import { BalanceGateTeam, shouldShowBalanceTab } from './balanceGate'

export interface TeamPageTabGates {
  showBilling: boolean
  showBalance: boolean
  /**
   * SN-4709 × SN-8081 — the Registration tab's Player Finder sub-tab. Probe-driven
   * like Balance and just as tenant-neutral, but BUDGET-INDEPENDENT: any team
   * registration the caller may manage qualifies, Team-Managed Budget or not.
   */
  showPlayerFinder: boolean
  /**
   * The Registration tab itself — shown whenever ANY of its sub-tabs is, including
   * when Player Finder is the only one (a non-budget team on Canlan).
   */
  showRegistration: boolean
}

/**
 * The Team page's Registration-tab gates in one testable place
 * (pages/Team/index.jsx consumes this — the D32 regression pin runs against
 * the REAL production conditions, not a copy).
 *
 * - Billing is shown to anyone with `permission.update`, on every tenant (it
 *   was SportNinja-only until SN-8453).
 * - Balance is TENANT-NEUTRAL (D32): probe-driven only — no tenant identity,
 *   no permission.update (which would hide the tab from bucket reps — R3).
 *   On SportNinja both tabs may render side by side.
 * - Player Finder is gated exactly like Balance, on its own probe (the settings
 *   GET, which answers 403 → no entries for a caller with no authority).
 * - SN-8453 — Player Finder also needs `playerFinderEnabled` (Canlan-only).
 *   Balance and Billing are unaffected by it.
 */
export const teamPageTabGates = ({
  item,
  permissionUpdate,
  balanceRegistrations,
  playerFinderRegistrations,
  playerFinderEnabled,
}: {
  item: BalanceGateTeam | null | undefined
  permissionUpdate: boolean
  balanceRegistrations: readonly unknown[] | null | undefined
  playerFinderRegistrations?: readonly unknown[] | null
  playerFinderEnabled: boolean
}): TeamPageTabGates => {
  const showBilling = Boolean(
    permissionUpdate && !(item?.is_placeholder ?? false)
  )
  const showBalance = shouldShowBalanceTab(item, balanceRegistrations)
  // Same probe predicate as Balance — a loaded, non-placeholder team with at
  // least one entry — applied to the Player Finder probe's own answer.
  const showPlayerFinder =
    playerFinderEnabled && shouldShowBalanceTab(item, playerFinderRegistrations)

  return {
    showBilling,
    showBalance,
    showPlayerFinder,
    showRegistration: showBilling || showBalance || showPlayerFinder,
  }
}
